import type { Node as ProseNode } from '@tiptap/pm/model';
import { splitFindingRefs } from '../../features/findings/findingRefs';
import type { FindingRef } from '../../features/findings/findingRefs';

/**
 * Where the Befund references sit in an editor document.
 *
 * Split out of the plugin and kept pure so the mapping from text offsets to
 * ProseMirror positions — the part that is easy to get quietly wrong by one —
 * can be checked in a test.
 */

/** One reference, as the two ranges a renderer marks it with. */
export interface FindingRefRange {
    /** Document positions of the whole reference — what gets the ring. */
    from: number;
    to: number;
    /** Document positions of the Befundnummer alone — what gets the bold. */
    numberFrom: number;
    numberTo: number;
    /** The reference as written, for a tooltip or a click handler. */
    text: string;
    /** What it says, for resolving it against the project's records. */
    ref: FindingRef;
}

/**
 * Every reference in the document, in order.
 *
 * Scanned per TEXT NODE rather than over the document's whole text: a text
 * node's start position is known, so `pos + offset` is the document position. A
 * reference can never straddle two of them here — the schema carries no marks,
 * so a paragraph is ONE text node.
 */
export function findingRefRanges(doc: ProseNode): FindingRefRange[] {
    const ranges: FindingRefRange[] = [];
    doc.descendants((node, pos) => {
        if (!node.isText || !node.text) {
            // Keep walking: paragraphs and hard breaks hold the text nodes.
            return true;
        }
        let offset = 0;
        for (const segment of splitFindingRefs(node.text, {
            withDescription: true
        })) {
            if (segment.kind === 'ref') {
                const from = pos + offset;
                const numberFrom = from + segment.numberStart;
                ranges.push({
                    from,
                    to: from + segment.text.length,
                    numberFrom,
                    numberTo: numberFrom + segment.number.length,
                    text: segment.text,
                    ref: {
                        number: segment.number,
                        ...(segment.area ? { area: segment.area } : {}),
                        ...(segment.planumNumber
                            ? { planumNumber: segment.planumNumber }
                            : {}),
                        ...(segment.profileNumber
                            ? { profileNumber: segment.profileNumber }
                            : {}),
                        ...(segment.overallDescription
                            ? { overallDescription: true as const }
                            : {})
                    }
                });
            }
            offset += segment.text.length;
        }
        return true;
    });
    return ranges;
}

/** The reference at a document position, if the position is inside one. */
export function findingRefAt(
    ranges: FindingRefRange[],
    pos: number
): FindingRefRange | null {
    return ranges.find((range) => pos >= range.from && pos <= range.to) ?? null;
}
